/**
 * Unified Container Styling System
 * Centralizes all container styling patterns for consistency
 * Eliminates duplicate className patterns across components
 */

export type ContainerVariant = 'default' | 'error' | 'loading' | 'success' | 'warning';
export type ContainerSize = 'sm' | 'md' | 'lg' | 'xl';
export type ContainerTheme = 'light' | 'dark' | 'auto';

export interface ContainerStyleOptions {
  variant?: ContainerVariant;
  size?: ContainerSize;
  theme?: ContainerTheme;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  border?: boolean;
  rounded?: boolean;
  shadow?: boolean;
  interactive?: boolean;
  className?: string;
}

/**
 * Unified container styling system
 * Provides consistent styling across all chart components
 */
export class ContainerStyles {
  private static readonly BASE_CLASSES = 'rounded-lg transition-colors';
  
  private static readonly VARIANT_STYLES = {
    default: {
      light: 'bg-white border-gray-200 text-gray-900',
      dark: 'bg-gray-800 border-gray-700 text-white',
      auto: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white'
    },
    error: {
      light: 'bg-red-50 border-red-200 text-red-900',
      dark: 'bg-red-900/20 border-red-700 text-red-100',
      auto: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-900 dark:text-red-100'
    },
    loading: {
      light: 'bg-blue-50 border-blue-200 text-blue-900',
      dark: 'bg-blue-900/20 border-blue-700 text-blue-100',
      auto: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 text-blue-900 dark:text-blue-100'
    },
    success: {
      light: 'bg-green-50 border-green-200 text-green-900',
      dark: 'bg-green-900/20 border-green-700 text-green-100',
      auto: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 text-green-900 dark:text-green-100'
    },
    warning: {
      light: 'bg-yellow-50 border-yellow-200 text-yellow-900',
      dark: 'bg-yellow-900/20 border-yellow-700 text-yellow-100',
      auto: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700 text-yellow-900 dark:text-yellow-100'
    }
  };

  private static readonly SIZE_STYLES = {
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
    xl: 'p-8'
  };

  private static readonly PADDING_STYLES = {
    none: 'p-0',
    sm: 'p-2',
    md: 'p-4',
    lg: 'p-6'
  };

  private static readonly SHADOW_STYLES = {
    none: '',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg'
  };

  /**
   * Generate container className based on options
   */
  static getContainerClass(options: ContainerStyleOptions = {}): string {
    const {
      variant = 'default',
      size = 'md',
      theme = 'auto',
      padding,
      border = true,
      rounded = true,
      shadow = false,
      interactive = false,
      className = ''
    } = options;

    const classes: string[] = [];

    // Base classes
    if (rounded) {
      classes.push('rounded-lg');
    }

    // Transition for theme changes
    classes.push('transition-colors');

    // Variant and theme styles
    const variantStyle = this.VARIANT_STYLES[variant][theme];
    classes.push(variantStyle);

    // Border
    if (border) {
      classes.push('border');
    }

    // Padding
    if (padding) {
      classes.push(this.PADDING_STYLES[padding]);
    } else {
      classes.push(this.SIZE_STYLES[size]);
    }

    // Shadow
    if (shadow) {
      classes.push(this.SHADOW_STYLES.md);
    }

    // Interactive styles
    if (interactive) {
      classes.push('hover:shadow-md transition-shadow cursor-pointer');
    }

    // Custom className
    if (className) {
      classes.push(className);
    }

    return classes.join(' ');
  }

  /**
   * Get chart container styles (most common pattern)
   */
  static getChartContainerClass(options: Partial<ContainerStyleOptions> = {}): string {
    return this.getContainerClass({
      variant: 'default',
      size: 'md',
      theme: 'auto',
      border: true,
      rounded: true,
      shadow: false,
      ...options
    });
  }

  /**
   * Get error container styles
   */
  static getErrorContainerClass(options: Partial<ContainerStyleOptions> = {}): string {
    return this.getContainerClass({
      variant: 'error',
      size: 'lg',
      theme: 'auto',
      border: true,
      rounded: true,
      shadow: false,
      ...options
    });
  }

  /**
   * Get loading container styles
   */
  static getLoadingContainerClass(options: Partial<ContainerStyleOptions> = {}): string {
    return this.getContainerClass({
      variant: 'loading',
      size: 'md',
      theme: 'auto',
      border: true,
      rounded: true,
      shadow: false,
      ...options
    });
  }

  /**
   * Get success container styles
   */
  static getSuccessContainerClass(options: Partial<ContainerStyleOptions> = {}): string {
    return this.getContainerClass({
      variant: 'success',
      size: 'md',
      theme: 'auto',
      border: true,
      rounded: true,
      shadow: false,
      ...options
    });
  }

  /**
   * Get warning container styles
   */
  static getWarningContainerClass(options: Partial<ContainerStyleOptions> = {}): string {
    return this.getContainerClass({
      variant: 'warning',
      size: 'md',
      theme: 'auto',
      border: true,
      rounded: true,
      shadow: false,
      ...options
    });
  }

  /**
   * Get interactive container styles (for clickable elements)
   */
  static getInteractiveContainerClass(options: Partial<ContainerStyleOptions> = {}): string {
    return this.getContainerClass({
      variant: 'default',
      size: 'md',
      theme: 'auto',
      border: true,
      rounded: true,
      shadow: true,
      interactive: true,
      ...options
    });
  }
}

/**
 * Pre-configured container class generators for common use cases
 */
export const ChartContainer = {
  default: () => ContainerStyles.getChartContainerClass(),
  error: () => ContainerStyles.getErrorContainerClass(),
  loading: () => ContainerStyles.getLoadingContainerClass(),
  success: () => ContainerStyles.getSuccessContainerClass(),
  warning: () => ContainerStyles.getWarningContainerClass(),
  interactive: () => ContainerStyles.getInteractiveContainerClass()
};

/**
 * Hook for dynamic container styling
 */
export function useContainerStyles(options: ContainerStyleOptions = {}) {
  return {
    containerClass: ContainerStyles.getContainerClass(options),
    chartContainerClass: ContainerStyles.getChartContainerClass(options),
    errorContainerClass: ContainerStyles.getErrorContainerClass(options),
    loadingContainerClass: ContainerStyles.getLoadingContainerClass(options),
    successContainerClass: ContainerStyles.getSuccessContainerClass(options),
    warningContainerClass: ContainerStyles.getWarningContainerClass(options),
    interactiveContainerClass: ContainerStyles.getInteractiveContainerClass(options)
  };
}
